import { useEffect, useMemo, useState } from 'react';
import {
  AutocompleteInput,
  ArrayInput,
  AutocompleteArrayInput,
  BooleanField,
  BooleanInput,
  CreateButton,
  Datagrid,
  EditButton,
  FormDataConsumer,
  ReferenceArrayInput,
  required,
  SaveButton,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { ListLive } from '@react-admin/ra-realtime';
import {
  Box, Card, CardContent, CircularProgress, FormControlLabel, Grid, Switch, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { JsonInput } from 'react-admin-json-view';
import _ from 'lodash';
import authProvider from '../../../utils/authProvider';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { StabilityAlert, StabilityLevel } from '../../../components/StabilityAlert';
import { ClientConfigChangedPreviewTree } from '../../../components/clientConfig/ClientConfigChangedPreviewTree';
import {
  AppDefinition,
  AppKeyDefinition,
  ChangedPreviewNode,
  buildChangedPreviewTree,
  buildConfigFromDefinitionsWithSources,
  flattenChangedLeafPaths,
  getChangedObject,
} from '../../../lib/clientConfigHelpers';
import {
  useAllClientConfigGroupsQuery,
  useClientConfigAppDefaultConfigV0,
} from '../../../lib/clientConfigReactQuery';
import { AppConfigDefinitionType, ArrayElement } from '../../../lib/constants/typedApiReturnTypes';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import { DateFieldWithTime } from '../../../components/fields/DateFieldWithTime';
import { ClientConfigGroupChip } from '../../../components/clientConfig/ClientConfigGroupChip';
import CustomBulkActionButtons from '../../../components/buttons/CustomBulkActionButtons';
import { ClientConfigSchemaFieldDefinition, ConfigFieldByType } from '../../../lib/clientConfigSchemaField';

type SourcesByPath = Record<string, string[]>;

const getGroupIdsFromRecord = (record: any): string[] => {

  const definitions = record?.clientConfig?.app?.[0]?.definitions || [];
  return definitions
    .filter((definition: any) => definition?.type === 'group' && definition?.group?._id)
    .map((definition: any) => definition.group._id);

};

const toPayload = (data: any) => {

  const currentGroupId = data?._id || data?.id;
  const currentCustomerId = getCurrentCustomer()?.value;
  const isCustomerSpecific = data?.customerSpecific === true;
  const keyDefinitions: AppDefinition[] = (data.keyDefinitions || [])
    .filter((definition: any) => definition?.key)
    .map((definition: any) => ({
      type: 'key',
      key: definition.key,
      value: definition.value,
      ...(definition.editableBy ? { editableBy: definition.editableBy } : {}),
    }));
  const groupDefinitions: AppDefinition[] = (data.groupIds || [])
    .filter((groupId: string) => !currentGroupId || groupId !== currentGroupId)
    .map((groupId: string) => ({
      type: 'group',
      group: {
        _id: groupId,
        _ref: 'ClientConfigGroup',
      },
    }));

  const payload = {
    name: data.name,
    customer: isCustomerSpecific && currentCustomerId ? {
      _id: currentCustomerId,
      _ref: 'Customer',
    } : null,
    clientConfig: {
      app: [
        {
          definitions: [
            ...keyDefinitions,
            ...groupDefinitions,
          ],
        },
      ],
    },
  };
  return payload;

};

const noopClientConfigInlineKeyPath = (_path: string) => {

};

const EMPTY_INLINE_REMOVED_KEY_PATHS = new Set<string>();

const EMPTY_INITIAL_KEY_DEFINITIONS: AppKeyDefinition[] = [];

const GroupPreview = () => {

  const translate = useTranslate();
  const record = useRecordContext<any>();
  const { data: appDefault, isPending: loadingAppDefault } = useClientConfigAppDefaultConfigV0();
  const { data: groups = [], isPending: loadingGroups } = useAllClientConfigGroupsQuery();

  const defaultConfig = appDefault ? (appDefault.config || {}) : null;
  const previewLoading = loadingAppDefault || loadingGroups;

  const groupById = useMemo(() => _.keyBy(groups, '_id'), [groups]);
  const initialGroupIds = useMemo(() => getGroupIdsFromRecord(record), [record]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {translate('clientConfig.groups.preview.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('clientConfig.groups.preview.description')}
        </Typography>
        {previewLoading || defaultConfig === null ? (
          <CircularProgress size={24} />
        ) : (
          <FormDataConsumer>
            {({ formData }) => {

              const groupIds = formData.groupIds || [];
              const keyDefinitions = formData.keyDefinitions || [];
              const addedGroupIds = new Set<string>(groupIds.filter((groupId: string) => !initialGroupIds.includes(groupId)));
              const removedGroupIds = new Set(initialGroupIds.filter((groupId: string) => !groupIds.includes(groupId)));
              const definitions: AppDefinition[] = [
                ...keyDefinitions
                  .filter((definition: any) => definition?.key)
                  .map((definition: any) => ({
                    type: 'key',
                    key: definition.key,
                    value: definition.value,
                    ...(definition.editableBy ? { editableBy: definition.editableBy } : {}),
                  })),
                ...groupIds.map((groupId: string) => ({
                  type: 'group' as const,
                  group: { _id: groupId, _ref: 'ClientConfigGroup' as const },
                })),
              ];
              const previousDefinitions: AppDefinition[] = [
                ...keyDefinitions
                  .filter((definition: any) => definition?.key)
                  .map((definition: any) => ({
                    type: 'key',
                    key: definition.key,
                    value: definition.value,
                    ...(definition.editableBy ? { editableBy: definition.editableBy } : {}),
                  })),
                ...initialGroupIds.map((groupId: string) => ({
                  type: 'group' as const,
                  group: { _id: groupId, _ref: 'ClientConfigGroup' as const },
                })),
              ];
              const previewBaseline = {};

              const {
                config: preview,
                sourcesByPath,
                overriddenSourcesByPath,
              } = buildConfigFromDefinitionsWithSources(previewBaseline, definitions, groupById);
              const previousPreviewData = buildConfigFromDefinitionsWithSources(previewBaseline, previousDefinitions, groupById);
              const changed = getChangedObject(previewBaseline, preview) || {};
              const changedLeafPaths = new Set(flattenChangedLeafPaths(changed).map((entry) => entry.path).filter(Boolean));
              const removedOnlyPaths = Object.keys(previousPreviewData.sourcesByPath)
                .filter((path) => {

                  const previousPathSources = previousPreviewData.sourcesByPath[path] || [];
                  const currentPathSources = (sourcesByPath as SourcesByPath)[path] || [];
                  const hadRemovedGroupBefore = previousPathSources.some((groupId) => removedGroupIds.has(groupId));
                  return hadRemovedGroupBefore && currentPathSources.length === 0 && !changedLeafPaths.has(path);

                });
              const combinedChanged = _.cloneDeep(changed);
              removedOnlyPaths.forEach((path) => {

                _.set(combinedChanged, path, _.get(previousPreviewData.config, path));

              });
              const changedTree: ChangedPreviewNode[] = buildChangedPreviewTree(combinedChanged, sourcesByPath);

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {changedTree.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {translate('clientConfig.groups.preview.noChanges')}
                    </Typography>
                  )}
                  <ClientConfigChangedPreviewTree
                    roots={changedTree}
                    sourcesByPath={sourcesByPath as SourcesByPath}
                    overriddenSourcesByPath={overriddenSourcesByPath as SourcesByPath}
                    previousSourcesByPath={previousPreviewData.sourcesByPath as SourcesByPath}
                    removedGroupIds={removedGroupIds}
                    addedGroupIds={addedGroupIds}
                    removedInlineKeyPathSet={EMPTY_INLINE_REMOVED_KEY_PATHS}
                    configGroupById={groupById}
                    selectedTargetType={null}
                    initialKeyDefinitions={EMPTY_INITIAL_KEY_DEFINITIONS}
                    onRestoreInlineKey={noopClientConfigInlineKeyPath}
                    onStageInlineKeyRemoval={noopClientConfigInlineKeyPath}
                  />
                </div>
              );

            }}
          </FormDataConsumer>
        )}
      </CardContent>
    </Card>
  );

};

const GroupToolbar = () => (
  <Toolbar sx={{ justifyContent: 'end' }}>
    <SaveButton />
  </Toolbar>
);

const KeyValueInputBySchema = (props: {
  source: string;
  schema: any;
}) => {

  const { source, schema } = props;
  if (!schema) {

    return <JsonInput source={source} reactJsonOptions={{ name: source, collapsed: false, displayDataTypes: false }} />;

  }
  const definition: ClientConfigSchemaFieldDefinition = {
    key: source,
    schema,
    mergeHistory: [],
  };
  return (
    <ConfigFieldByType
      definition={definition}
      fallback={<JsonInput source={source} reactJsonOptions={{ name: source, collapsed: false, displayDataTypes: false }}/>}
    />
  );

};

const GroupFormFields = () => {

  const record = useRecordContext<any>();
  const currentGroupId = record?._id || record?.id;
  const translate = useTranslate();
  const { data: appDefault, isPending: loadingKeys } = useClientConfigAppDefaultConfigV0();

  const keyChoices = useMemo(() => {

    const definitions = appDefault?.definition || [];
    const keys: string[] = definitions.map((definition) => definition.key);
    const uniqueKeys = _.uniq(keys).sort((a, b) => a.localeCompare(b));
    return uniqueKeys.map((key) => ({ id: key, name: key }));

  }, [appDefault]);

  const keyDefinitionsByKey = useMemo(
    () => _.keyBy(appDefault?.definition || [], 'key') as Record<string, ArrayElement<AppConfigDefinitionType>>,
    [appDefault],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextInput source="name" fullWidth validate={required()} label="general.fields.name" />
        <BooleanInput
          source="customerSpecific"
          defaultValue={Boolean(record?.customer?._id || record?.customerId)}
          label={translate('clientConfig.groups.form.customerSpecific')}
        />
        <ReferenceArrayInput source="groupIds" reference="client-config-groups" fullWidth>
          <AutocompleteArrayInput
            optionText="name"
            label={translate('clientConfig.groups.form.groups')}
            getOptionDisabled={(choice: any) => {

              if (!currentGroupId) {

                return false;

              }
              const choiceId = choice?._id || choice?.id;
              return choiceId === currentGroupId;

            }}
            parse={(value: any) => {

              if (!currentGroupId || !Array.isArray(value)) {

                return value;

              }
              return value.filter((entry: any) => {

                const entryId = typeof entry === 'string' ? entry : (entry?._id || entry?.id);
                return entryId !== currentGroupId;

              });

            }}
            renderTags={(value: readonly any[], getTagProps) => value.map((option: any, index: number) => {

              const id = option?._id || option?.id;
              return (
                <ClientConfigGroupChip
                  key={id}
                  group={{
                    _id: id,
                    name: option?.name || id,
                    customer: option?.customer,
                  }}
                  getTagProps={getTagProps}
                  index={index}
                />
              );

            })}
          />
        </ReferenceArrayInput>
        <ArrayInput source="keyDefinitions" label={translate('clientConfig.groups.form.keys')}>
          <SimpleFormIterator disableReordering>
            <AutocompleteInput
              source="key"
              choices={keyChoices}
              optionText="name"
              optionValue="id"
              label={translate('clientConfig.groups.form.key')}
              fullWidth
              validate={required()}
              isPending={loadingKeys}
            />
            <FormDataConsumer>
              {({ scopedFormData }) => {

                const selectedKey = scopedFormData?.key;
                const schema = selectedKey ? keyDefinitionsByKey[selectedKey]?.schema : null;
                return <KeyValueInputBySchema source="value" schema={schema} />;

              }}
            </FormDataConsumer>
            <AutocompleteArrayInput
              source="editableBy"
              choices={[
                { id: 'AssetGroup', name: 'AssetGroup' },
                { id: 'Device', name: 'Device' },
              ]}
              label={translate('clientConfig.groups.form.editableBy')}
              fullWidth
            />
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
      <Grid item xs={12} md={6}>
        <GroupPreview />
      </Grid>
    </Grid>
  );

};

const useDevAdminGuard = () => {

  const navigate = useNavigate();
  const notify = useNotify();
  const isDevAdmin = authProvider.isDeveloperAdmin();
  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

};

export const ClientConfigGroupList = () => {

  useDevAdminGuard();
  const translate = useTranslate();
  const [showGlobalClientConfigGroups, setShowGlobalClientConfigGroups] = useState(true);
  const listFilter = useMemo(
    () => ({ includeGlobalClientConfigGroups: showGlobalClientConfigGroups }),
    [showGlobalClientConfigGroups],
  );

  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <ListLive
        resource="client-config-groups"
        sort={{ field: 'name', order: 'ASC' }}
        filter={listFilter}
        empty={false}
        actions={(
          <TopToolbar>
            <FormControlLabel
              control={(
                <Switch
                  checked={showGlobalClientConfigGroups}
                  onChange={(_event, checked) => setShowGlobalClientConfigGroups(checked)}
                />
              )}
              label={translate('clientConfig.groups.list.showGlobalGroups')}
            />
            <CreateButton />
          </TopToolbar>
        )}
      >
        <Datagrid rowClick="toggleSelection" bulkActionButtons={<CustomBulkActionButtons/>}>
          <TextField source="name" />
          {showGlobalClientConfigGroups && (
            <BooleanField
              source="customerSpecific"
              label="clientConfig.groups.form.customerSpecific"
            />
          )}
          <DateFieldWithTime source="createdAt" label="general.fields.createdAt" timeOnHover={true}/>
          <DateFieldWithTime source="updatedAt" label="general.fields.updatedAt" timeOnHover={true}/>
          <EditButton />
        </Datagrid>
      </ListLive>
    </>
  );

};

export const ClientConfigGroupEdit = () => {

  useDevAdminGuard();
  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <EditWithSuccess title="clientConfig.menu.groups" transform={toPayload}>
        <SimpleForm toolbar={<GroupToolbar />}>
          <Box sx={{ width: '100%' }}>
            <GroupFormFields />
          </Box>
        </SimpleForm>
      </EditWithSuccess>
    </>
  );

};

export const ClientConfigGroupCreate = () => {

  useDevAdminGuard();
  return (
    <>
      <StabilityAlert stability={StabilityLevel.Unstable} />
      <CreateWithSuccess title="clientConfig.menu.groups" transform={toPayload}>
        <SimpleForm toolbar={<GroupToolbar />}>
          <Box sx={{ width: '100%' }}>
            <GroupFormFields />
          </Box>
        </SimpleForm>
      </CreateWithSuccess>
    </>
  );

};
